/**
 * Flashcard-specific database operations
 */

/**
 * Save flashcard decks from offline data
 * @param {EduQuizDB} db - Database instance
 * @param {Array} resources - Resources array
 * @param {string} FLASHCARD_DECK - Resource type constant
 */
export async function saveFlashcardDecks(db, resources, FLASHCARD_DECK) {
    const flashcardDecksData = [];
    const flashcardsCardsData = [];

    for (const resource of resources) {
        if (resource.type === FLASHCARD_DECK) {
            // Save flashcard deck
            flashcardDecksData.push({
                serverResourceId: resource.id,
                name: resource.name,
                description: resource.description || null,
            });

            // Save flashcard cards
            if (resource.cards && resource.cards.length > 0) {
                for (const card of resource.cards) {
                    // Extract UUIDs from question_images and answer_images
                    const questionImageUuids = card.question_images
                        ? card.question_images.map((img) => img.uuid)
                        : [];
                    const answerImageUuids = card.answer_images
                        ? card.answer_images.map((img) => img.uuid)
                        : [];

                    flashcardsCardsData.push({
                        serverId: card.id,
                        deckServerResourceId: resource.id,
                        front: card.question,
                        back: card.answer,
                        order: card.order,
                        studyBothSides: card.study_both_sides || false,
                        questionImageUuids: questionImageUuids,
                        answerImageUuids: answerImageUuids,
                        questionAudioUuid: card.question_audio_uuid || null,
                    });
                }
            }
        }
    }

    if (flashcardDecksData.length > 0) {
        console.log("Saving flashcard decks:", flashcardDecksData);
        await db.flashcardDecks.bulkAdd(flashcardDecksData);
    }

    if (flashcardsCardsData.length > 0) {
        console.log("Saving flashcard cards:", flashcardsCardsData);
        await db.flashcardsCards.bulkAdd(flashcardsCardsData);
    }
}

/**
 * Get flashcard cards for a specific deck
 * @param {EduQuizDB} db - Database instance
 * @param {number} deckId - Deck ID
 */
export async function getFlashcardsByDeck(db, deckId) {
    try {
        const flashcardsCards = await db.flashcardsCards
            .where("deckServerResourceId")
            .equals(deckId)
            .toArray();

        // Load assets for each card
        for (const card of flashcardsCards) {
            // Load question images
            if (card.questionImageUuids && card.questionImageUuids.length > 0) {
                const images = await db.getAssets(card.questionImageUuids);
                card.questionImages = images.map(img => ({
                    uuid: img.uuid,
                    url: img.url,
                    urlCachePWA: img.urlCachePWA,
                    extension: img.extension,
                }));
            } else {
                card.questionImages = [];
            }

            // Load answer images
            if (card.answerImageUuids && card.answerImageUuids.length > 0) {
                const images = await db.getAssets(card.answerImageUuids);
                card.answerImages = images.map(img => ({
                    uuid: img.uuid,
                    url: img.url,
                    urlCachePWA: img.urlCachePWA,
                    extension: img.extension,
                }));
            } else {
                card.answerImages = [];
            }

            // Load question audio
            if (card.questionAudioUuid) {
                const audio = await db.getAsset(card.questionAudioUuid);
                card.questionAudio = audio ? {
                    uuid: audio.uuid,
                    url: audio.url,
                    urlCachePWA: audio.urlCachePWA,
                    extension: audio.extension,
                } : null;
            } else {
                card.questionAudio = null;
            }
        }

        // Sort by order
        return flashcardsCards.sort((a, b) => (a.order || 0) - (b.order || 0));
    } catch (error) {
        console.error("Failed to get flashcard cards for deck:", error);
        throw error;
    }
}

/**
 * Get a flashcard deck by server resource ID
 * @param {EduQuizDB} db - Database instance
 * @param {number} serverResourceId - Server resource ID
 */
export async function getFlashcardDeck(db, serverResourceId) {
    try {
        return await db.flashcardDecks
            .where("serverResourceId")
            .equals(serverResourceId)
            .first();
    } catch (error) {
        console.error("Failed to get flashcard deck:", error);
        throw error;
    }
}

/**
 * Get a flashcard deck with its cards
 * @param {EduQuizDB} db - Database instance
 * @param {number} serverResourceId - Server resource ID
 */
export async function getFlashcardDeckWithCards(db, serverResourceId) {
    try {
        const deck = await getFlashcardDeck(db, serverResourceId);
        if (!deck) {
            return null;
        }

        const cards = await getFlashcardsByDeck(db, serverResourceId);

        return {
            ...deck,
            cards,
        };
    } catch (error) {
        console.error("Failed to get flashcard deck with cards:", error);
        throw error;
    }
}
